import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { date, dateTime, money, num, qty, shortDate } from '@/lib/format';
import { ChoiceGroup, Chips, DataTable, DeskHead, MoreHint, Pager, PhoneBar, SearchBox, Segmented, Sheet, type Col } from '../admin/kit/Kit';
import { useApi, useAutoMore, usePaged, useUrlState } from '../admin/kit/hooks';
import { lineKind, settlementType, taobaoStatus } from './status';
import type { TaobaoOptions, TaobaoOrder, TaobaoOutbound, TaobaoReturn } from './types';
import { TaobaoDetail } from './TaobaoDetail';
import s from './TaobaoPage.module.css';

type Tab = 'orders' | 'outbound' | 'returns';
const TABS: { key: Tab; label: string }[] = [
  { key: 'orders', label: '订单' },
  { key: 'outbound', label: '出库记录' },
  { key: 'returns', label: '退货记录' },
];
const FILTER_KEYS = ['settle', 'wh', 'from', 'to'] as const;
type Filters = Partial<Record<(typeof FILTER_KEYS)[number], string>>;

const unit = (t: string) => (t === 'BUNDLE' ? '套' : '个');

/** 订单卡：状态 / 客户 / 内容（最多 2 行）/ 日期·仓库·开单人 + 净额 / 结算与未收 */
function OrderCard({ o, to, selected }: { o: TaobaoOrder; to: string; selected?: boolean }) {
  const st = taobaoStatus[o.status] || { label: o.status, tone: 'none' as const };
  const settle = settlementType[o.settlementType];
  const unpaid = num(o.unpaidAmount);
  return (
    <Link to={to} className={`${s.card} ${selected ? s.sel : ''}`}>
      <div className={s.h}><b className="tnum">{o.orderNo}</b><Tag tone={st.tone} sm>{st.label}</Tag></div>
      <div className={s.c}>{o.customer.name}</div>
      {o.lines.length > 0 && (
        <ul className={s.l}>
          {o.lines.slice(0, 2).map((l) => <li key={l.id}><i>{lineKind(l.lineType)}</i><span>{l.bundle?.name || l.product?.name}</span><b>{qty(l.quantity)} {unit(l.lineType)}</b></li>)}
          {o.lines.length > 2 && <li className={s.more}>+{o.lines.length - 2} 项</li>}
        </ul>
      )}
      <div className={s.d}>
        <span className="tnum">{shortDate(o.orderDate)}</span>
        <span>{o.warehouse.name}</span>
        <span>{o.createdBy.name}开单</span>
        <b className={`tnum ${s.amt}`}>{money(o.netAmount)}</b>
      </div>
      <div className={s.f}>
        {settle && <Dot tone={settle.dot}>{settle.label}{o.paymentMethod ? ` · ${o.paymentMethod}` : ''}</Dot>}
        {o.status !== 'DRAFT' && o.status !== 'CANCELLED' && (unpaid > 0 ? <span className={s.unpaid}>未收 <b className="tnum">{money(unpaid)}</b></span> : <span className={s.paid}>已收清</span>)}
        {num(o.returnAmount) > 0 && <span className={s.ret}>退货 {money(o.returnAmount)}</span>}
      </div>
    </Link>
  );
}

function OutboundCard({ r }: { r: TaobaoOutbound }) {
  return (
    <Link to={`/taobao/${r.orderId}`} className={s.card}>
      <div className={s.h}><b>{r.product.name}</b><span className={`tnum ${s.qty}`}>{qty(r.quantity)} 个</span></div>
      <p className={s.sub2}><span className="tnum">{r.product.code}</span> · {lineKind(r.orderLine.lineType)} {r.orderLine.bundle?.name || r.orderLine.product?.name}</p>
      <div className={s.d}>
        <span className="tnum">{r.order.orderNo}</span>
        <span>{r.order.customer.name}</span>
      </div>
      <div className={s.f}><span>{r.warehouse.name}</span><span>{r.createdBy.name}</span><span className={`tnum ${s.right}`}>{dateTime(r.createdAt)}</span></div>
    </Link>
  );
}

function ReturnCard({ r }: { r: TaobaoReturn }) {
  return (
    <Link to={`/taobao/${r.orderId}`} className={s.card}>
      <div className={s.h}><b className="tnum">{r.returnNo}</b><b className={`tnum ${s.retAmt}`}>{money(r.amount)}</b></div>
      <div className={s.c}>{r.order.customer.name}</div>
      <ul className={s.l}>
        {r.lines.slice(0, 2).map((x) => <li key={x.id}><i>{lineKind(x.orderLine.lineType)}</i><span>{x.orderLine.bundle?.name || x.orderLine.product?.name}</span><b>{qty(x.quantity)} {unit(x.orderLine.lineType)}</b></li>)}
        {r.lines.length > 2 && <li className={s.more}>+{r.lines.length - 2} 项</li>}
      </ul>
      <div className={s.f}><span className="tnum">原单 {r.order.orderNo}</span><span>入 {r.order.warehouse.name}</span><span className={`tnum ${s.right}`}>{dateTime(r.returnedAt)}</span></div>
    </Link>
  );
}

function FilterSheet({ tab, value, sheet, onApply, onClose }: { tab: Tab; value: Filters; sheet: boolean; onApply: (f: Filters) => void; onClose: () => void }) {
  const [f, setF] = useState<Filters>(value);
  const opts = useApi<TaobaoOptions>('/api/taobao/options');
  const picked = Object.values(f).filter(Boolean).length;
  return (
    <Sheet title="筛选" sheet={sheet} onClose={onClose} onReset={() => setF({})} footer={<Button variant="primary" size="lg" block onClick={() => onApply(f)}>应用{picked ? `（${picked} 项）` : ''}</Button>}>
      {tab === 'orders' && <ChoiceGroup title="结算方式" value={f.settle} onChange={(v) => setF({ ...f, settle: v })} options={[{ value: 'CASH', label: '现结' }, { value: 'MONTHLY', label: '月结' }]} />}
      <ChoiceGroup title={tab === 'orders' ? '出库仓库' : '仓库'} value={f.wh} onChange={(v) => setF({ ...f, wh: v })} options={(opts.data?.warehouses || []).map((w) => ({ value: w.id, label: w.name }))} />
      <section className={s.dates}>
        <h4>{tab === 'orders' ? '下单日期' : '出库日期'}</h4>
        <div>
          <input type="date" value={f.from || ''} max={f.to || undefined} onChange={(e) => setF({ ...f, from: e.target.value || undefined })} aria-label="开始日期" />
          <span>至</span>
          <input type="date" value={f.to || ''} min={f.from || undefined} onChange={(e) => setF({ ...f, to: e.target.value || undefined })} aria-label="结束日期" />
        </div>
      </section>
    </Sheet>
  );
}

/**
 * 淘宝店（独立业务线，taobao.manage）：订单 / 出库记录 / 退货记录三个页签。
 * - 手机：卡片列表 + 底部筛选 sheet，点卡进详情（深层页隐藏 Tab 栏）；
 * - iPad：订单页签是列表 + 详情分栏，另外两个页签是表格；
 * - 桌面：表格 + 翻页，点行进详情。
 * 开单、出库、退货都在老版。
 */
export function TaobaoPage() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const [sp, set] = useUrlState();
  const [panel, setPanel] = useState(false);
  const tab = (TABS.find((t) => t.key === sp.get('tab'))?.key || 'orders') as Tab;
  const q = sp.get('q') || '';
  const status = sp.get('status') || 'all';
  const selectedId = sp.get('id') || undefined;
  const filters: Filters = Object.fromEntries(FILTER_KEYS.map((k) => [k, sp.get(k) || undefined]).filter(([, v]) => v));
  const phone = ff === 'phone';
  // 手机和 iPad 分栏的卡片列表滚到底追加；桌面和 iPad 的表格翻页
  const append = phone || (ff === 'tablet' && tab === 'orders');
  const allowed = can('taobao.manage');

  const orders = usePaged<TaobaoOrder>(allowed && tab === 'orders' ? '/api/taobao/orders' : null,
    { keyword: q || undefined, status: status === 'all' ? undefined : status, settlementType: filters.settle, warehouseId: filters.wh, from: filters.from, to: filters.to }, 20, append);
  const outbound = usePaged<TaobaoOutbound>(allowed && tab === 'outbound' ? '/api/taobao/outbound-records' : null,
    { keyword: q || undefined, warehouseId: filters.wh, from: filters.from, to: filters.to }, 30, append);
  const returns = usePaged<TaobaoReturn>(allowed && tab === 'returns' ? '/api/taobao/returns' : null, { keyword: q || undefined }, 20, append);
  const list = tab === 'orders' ? orders : tab === 'outbound' ? outbound : returns;
  const sentinel = useAutoMore(append, list.loadMore);

  if (!allowed) return <Empty icon="lock" title="没有淘宝店权限">需要「管理淘宝店」权限，请联系管理员。</Empty>;

  const activeFilters = FILTER_KEYS.filter((k) => filters[k] && (tab !== 'returns') && (k !== 'settle' || tab === 'orders'));
  const newOrder = <Link className={s.newBtn} to="/taobao/new"><Icon name="plus" />淘宝店开单</Link>;
  const seg = <Segmented items={TABS} value={tab} onChange={(k) => set({ tab: k === 'orders' ? undefined : k, id: undefined, status: undefined, q: undefined })} />;
  const search = <SearchBox value={q} onSearch={(v) => set({ q: v || undefined, id: undefined })} className={s.search}
    placeholder={tab === 'orders' ? '订单号 / 客户 / 产品或套装' : tab === 'outbound' ? '订单号 / 客户 / 产品' : '退货单号 / 原订单号 / 客户'} />;
  const filterBtn = tab !== 'returns' && (
    <button className={`${s.fbtn} ${activeFilters.length ? s.fon : ''}`} onClick={() => setPanel(true)}>
      <Icon name="filter" />筛选{activeFilters.length > 0 && <span className={s.fbadge}>{activeFilters.length}</span>}
    </button>
  );
  const statusChips = tab === 'orders' && (
    <Chips value={status} onChange={(k) => set({ status: k === 'all' ? undefined : k, id: undefined })}
      items={[{ key: 'all', label: '全部' }, ...Object.entries(taobaoStatus).map(([k, v]) => ({ key: k, label: v.label }))]} />
  );
  const applied = activeFilters.length > 0 && (
    <div className={s.applied}>
      {activeFilters.map((k) => (
        <button key={k} className={s.achip} onClick={() => set({ [k]: undefined })}>
          {k === 'settle' ? settlementType[filters.settle!]?.label : k === 'wh' ? '指定仓库' : k === 'from' ? `${filters.from} 起` : `至 ${filters.to}`}
          <Icon name="x" size={14} />
        </button>
      ))}
      <button className={s.clr} onClick={() => set(Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])))}>清空</button>
    </div>
  );
  const panelEl = panel && (
    <FilterSheet tab={tab} value={filters} sheet={phone} onClose={() => setPanel(false)}
      onApply={(f) => { setPanel(false); set({ ...Object.fromEntries(FILTER_KEYS.map((k) => [k, f[k]])), id: undefined }); }} />
  );

  const filtered = !!q || status !== 'all' || activeFilters.length > 0;
  const body = (() => {
    if (list.error && !list.items.length) return <ErrorState title="加载失败" message={list.error.message} detail={`GET ${tab === 'orders' ? '/api/taobao/orders' : tab === 'outbound' ? '/api/taobao/outbound-records' : '/api/taobao/returns'}`} onRetry={list.reload} />;
    if (list.loading && !list.items.length) return <div className={s.pad}><Skeleton /><Skeleton /><Skeleton lines={3} /></div>;
    if (!list.items.length) {
      const what = tab === 'orders' ? '淘宝店订单' : tab === 'outbound' ? '出库记录' : '退货记录';
      return (
        <Empty icon={tab === 'returns' ? 'return' : 'store'} title={filtered ? `没有符合条件的${what}` : `还没有${what}`}>
          {filtered ? '换个关键词，或者清空筛选再看看。' : tab === 'orders' ? '开单并确认出库后会出现在这里。' : tab === 'outbound' ? '订单确认出库后，每个实际出库的产品一行（套装按组件拆开），都关联库存流水。' : '对已出库的订单办理退货后，会出现在这里。'}
        </Empty>
      );
    }
    return null;
  })();

  const cards = (
    <>
      <div className={s.listbar}>{list.total} 条{q && ` · 搜索「${q}」`}</div>
      {body ||
        (tab === 'orders'
          ? orders.items.map((o) => <OrderCard key={o.id} o={o} to={ff === 'tablet' ? `/taobao?${new URLSearchParams({ ...Object.fromEntries(sp), id: o.id })}` : `/taobao/${o.id}`} selected={ff === 'tablet' && o.id === selectedId} />)
          : tab === 'outbound'
            ? outbound.items.map((r) => <OutboundCard key={r.id} r={r} />)
            : returns.items.map((r) => <ReturnCard key={r.id} r={r} />))}
      <MoreHint hasNext={list.hasNext} loading={list.loading} count={list.items.length} sentinel={sentinel} />
    </>
  );

  /* ------------------------------ 手机 ------------------------------ */
  if (phone) {
    return (
      <div className={s.phone}>
        <PhoneBar large back="/apps" backLabel="应用" title="淘宝店" right={<Link className={s.pIcon} to="/taobao/new" aria-label="淘宝店开单"><Icon name="plus" size={22} /></Link>} />
        <div className={s.pseg}>{seg}</div>
        <div className={s.psearch}>{search}</div>
        {(statusChips || filterBtn) && <div className={s.chipbar}>{statusChips || <span className={s.sp} />}{filterBtn}</div>}
        {applied}
        <div className={s.pbody}>{cards}</div>
        {panelEl}
      </div>
    );
  }

  /* ------------------------------ iPad：订单 列表 + 详情 ------------------------------ */
  if (ff === 'tablet' && tab === 'orders') {
    return (
      <div className={s.split}>
        <section className={s.listCol}>
          <header className={s.lh}><h1>淘宝店<small className="tnum">{orders.total}</small></h1>{newOrder}</header>
          <div className={s.lseg}>{seg}</div>
          <div className={s.lsearch}>{search}{filterBtn}</div>
          <div className={s.lchips}>{statusChips}</div>
          {applied}
          <div className={s.lbody}>{cards}</div>
        </section>
        <div className={s.divider} />
        <section className={s.detailCol}>
          {selectedId ? <TaobaoDetail id={selectedId} embedded /> : <Empty icon="cards" title="选择左侧订单查看详情">详情在这里打开，不用跳页。</Empty>}
        </section>
        {panelEl}
      </div>
    );
  }

  /* --------------------------- 桌面 / iPad 表格 --------------------------- */
  const orderCols: Col<TaobaoOrder>[] = [
    { key: 'no', title: '订单号', w: 190, fix: true, render: (o) => <b className="tnum">{o.orderNo}</b> },
    { key: 'd', title: '下单日期', w: 104, render: (o) => <span className="tnum">{date(o.orderDate)}</span> },
    { key: 'c', title: '客户名称', w: 150, render: (o) => o.customer.name },
    { key: 'l', title: '订单详情', w: 260, render: (o) => <span className={s.two}>{o.lines.map((l) => `${lineKind(l.lineType)}：${l.bundle?.name || l.product?.name || '—'} - ${qty(l.quantity)}`).join('；')}</span> },
    { key: 't', title: '订单总价', w: 116, align: 'r', render: (o) => money(o.totalAmount) },
    { key: 'dis', title: '优惠', w: 96, align: 'r', render: (o) => (num(o.discountAmount) > 0 ? `-${money(o.discountAmount)}` : <span className="em">—</span>) },
    { key: 'ret', title: '退货金额', w: 104, align: 'r', render: (o) => (num(o.returnAmount) > 0 ? <span className={s.warnT}>{money(o.returnAmount)}</span> : <span className="em">—</span>) },
    { key: 'net', title: '应收净额', w: 116, align: 'r', render: (o) => <b>{money(o.netAmount)}</b> },
    { key: 'st', title: '结算', w: 120, render: (o) => { const x = settlementType[o.settlementType]; return x ? <Dot tone={x.dot}>{x.label}{o.paymentMethod ? ` · ${o.paymentMethod}` : ''}</Dot> : o.settlementType; } },
    { key: 'paid', title: '已收', w: 110, align: 'r', render: (o) => money(o.paidAmount) },
    { key: 'unpaid', title: '未收', w: 110, align: 'r', render: (o) => (num(o.unpaidAmount) > 0 ? <span className={s.warnT}>{money(o.unpaidAmount)}</span> : <span className="em">{money(0)}</span>) },
    { key: 'wh', title: '仓库', w: 100, render: (o) => o.warehouse.name },
    { key: 'by', title: '开单人', w: 84, render: (o) => o.createdBy.name },
    { key: 'status', title: '状态', w: 92, render: (o) => { const x = taobaoStatus[o.status]; return <Tag tone={x?.tone || 'none'} sm>{x?.label || o.status}</Tag>; } },
  ];
  const outCols: Col<TaobaoOutbound>[] = [
    { key: 't', title: '出库时间', w: 120, render: (r) => <span className="tnum">{dateTime(r.createdAt)}</span> },
    { key: 'no', title: '淘宝店订单号', w: 190, render: (r) => <span className="tnum">{r.order.orderNo}</span> },
    { key: 'c', title: '客户名称', w: 150, render: (r) => r.order.customer.name },
    { key: 'item', title: '销售项目', w: 200, render: (r) => <span>{r.orderLine.lineType === 'BUNDLE' && <Tag tone="violet" sm>套装</Tag>} {r.orderLine.bundle?.name || r.orderLine.product?.name || '—'}</span> },
    { key: 'p', title: '实际出库产品', w: 220, render: (r) => <span><span className="tnum em">{r.product.code}</span> {r.product.name}</span> },
    { key: 'q', title: '出库数量', w: 90, align: 'r', render: (r) => qty(r.quantity) },
    { key: 'wh', title: '仓库', w: 110, render: (r) => r.warehouse.name },
    { key: 'by', title: '操作人', w: 90, render: (r) => r.createdBy.name },
  ];
  const retCols: Col<TaobaoReturn>[] = [
    { key: 'no', title: '退货单号', w: 190, fix: true, render: (r) => <b className="tnum">{r.returnNo}</b> },
    { key: 't', title: '退货时间', w: 120, render: (r) => <span className="tnum">{dateTime(r.returnedAt)}</span> },
    { key: 'o', title: '原淘宝店订单', w: 190, render: (r) => <span className="tnum">{r.order.orderNo}</span> },
    { key: 'c', title: '客户名称', w: 150, render: (r) => r.order.customer.name },
    { key: 'l', title: '退货明细', w: 260, render: (r) => <span className={s.two}>{r.lines.map((x) => `${lineKind(x.orderLine.lineType)}：${x.orderLine.bundle?.name || x.orderLine.product?.name || '—'} × ${qty(x.quantity)}`).join('；')}</span> },
    { key: 'a', title: '退货金额', w: 116, align: 'r', render: (r) => money(r.amount) },
    { key: 'wh', title: '入库仓库', w: 110, render: (r) => r.order.warehouse.name },
    { key: 'by', title: '操作人', w: 90, render: (r) => r.createdBy.name },
    { key: 'n', title: '备注', w: 200, render: (r) => r.note || <span className="em">—</span> },
  ];
  const table =
    tab === 'orders' ? <DataTable cols={orderCols} rows={orders.items} rowKey={(o) => o.id} onRow={(o) => nav(`/taobao/${o.id}`)} />
    : tab === 'outbound' ? <DataTable cols={outCols} rows={outbound.items} rowKey={(r) => r.id} onRow={(r) => nav(`/taobao/${r.orderId}`)} />
    : <DataTable cols={retCols} rows={returns.items} rowKey={(r) => r.id} onRow={(r) => nav(`/taobao/${r.orderId}`)} />;
  const sub = tab === 'outbound' ? '每一行都关联库存流水，不另算库存；套装按组件拆开' : tab === 'returns' ? '退货确认后已恢复原仓库库存，并写入库存流水' : '简易开单、现结 / 月结、销售出库与退货';

  return (
    <div className={s.desk}>
      <DeskHead title="淘宝店" count={`共 ${list.total} 条`} sub={sub}>
        {seg}
        {search}
        {filterBtn}
        <Button icon="ref" onClick={list.reload}>刷新</Button>
        {newOrder}
      </DeskHead>
      {statusChips && <div className={s.dchips}>{statusChips}</div>}
      {applied}
      {body ? <div className={s.dbody}>{body}</div> : table}
      {!body && <Pager page={list.page} pageSize={list.pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGoto={list.goto} />}
      {panelEl}
    </div>
  );
}
